import {
  type ApiError,
  FetchError,
  type SsoDetails,
  type CreateSessionResponse,
  sessionStatusParam,
  sessionStatusExpired,
  type CurrentUser,
  type User,
  type CreateUserData,
  type UpdateUserData,
  type WorkflowStepInstanceCommentType,
  type IdAndTitle,
  type CategoryData,
  type CategoryType,
  type CreateTierData,
  type SpSite,
  type TierDetails,
  type UpdateTierData,
  type WorkflowTemplate,
  type WorkflowTemplateData,
  type ApplyWorkflowTemplateData,
  type TQRcode,
  type CreateGovernanceUnitData,
  type UpdateGovernanceUnitData,
  type QuizAnswerOption,
} from "../types";
import { getRelativeLocationUrl } from "~/helpers/navigation";
import { setSelectedGovernanceUnitId } from "~/helpers/governanceUnit";

type Method = "POST" | "DELETE" | "GET" | "PATCH" | "PUT";

const getCurrentUserEndpoint = "/api/v2/users/me";

async function fetchApi(
  endpoint: string,
  method: Method = "GET",
  body?: object | FormData | Blob,
) {
  let requestBody = null;
  let requestHeaders = undefined;

  if (body) {
    const isFormData = body instanceof FormData;
    const isBlob = body instanceof Blob;

    requestBody = isFormData || isBlob ? body : JSON.stringify(body);

    if (!isFormData) {
      requestHeaders = {
        "Content-Type": isBlob
          ? "application/octet-stream"
          : "application/json",
      };
    }
  }

  const res = await fetch(endpoint, {
    method,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!res.ok) {
    if (
      res.status === 401 &&
      window.location.pathname !== "/login" && // We handle 401's locally on the login page
      endpoint !== getCurrentUserEndpoint && // We handle redirects for this endpoint in RouteAuthorized.tsx
      endpoint !== "/api/v2/credentials/password" && // Handle INVALID_CREDENTIALS locally
      endpoint !== "/api/v2/credentials/two-fa" // Handle INVALID_CREDENTIALS locally
    ) {
      const redirect = encodeURIComponent(
        getRelativeLocationUrl(window.location),
      );
      window.location.replace(
        `/login?${sessionStatusParam}=${sessionStatusExpired}&redirect=${redirect}`,
      );
    }
    const error = (await res.json()) as ApiError;
    throw new FetchError(error.code, error.message, error.details);
  }

  return res;
}

export async function createSession(
  email: string,
  password: string,
  token?: string,
) {
  const res = await fetchApi("/api/v2/sessions", "POST", {
    email,
    password,
    token,
  });
  return res.json() as Promise<CreateSessionResponse>;
}

export async function deleteSession() {
  await fetchApi("/api/v2/sessions", "DELETE");
  setSelectedGovernanceUnitId(null);
}

export async function generateTwoFa() {
  const res = await fetchApi("/api/v2/credentials/two-fa/generate", "POST");
  return res.json() as Promise<TQRcode>;
}

export async function enableTwoFa(token: string, secret: string) {
  await fetchApi("/api/v2/credentials/two-fa", "POST", { token, secret });
}

export async function disableTwoFa() {
  await fetchApi("/api/v2/credentials/two-fa", "DELETE");
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
) {
  await fetchApi("/api/v2/credentials/password", "PATCH", {
    currentPassword,
    newPassword,
  });
}

export async function forgotPassword(
  input: { email: string } | { token: string },
) {
  await fetchApi("/api/v2/credentials/password/forgot", "POST", input);
}

export async function resetPassword(token: string, password: string) {
  await fetchApi("/api/v2/credentials/password/reset", "POST", {
    password,
    token,
  });
}

export async function createUser(data: CreateUserData) {
  await fetchApi("/api/v2/users", "POST", data);
}

export async function updateUser(
  id: number,
  { categoryIds, ...data }: UpdateUserData,
): Promise<User> {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });

  if (categoryIds) {
    categoryIds.forEach((id) => formData.append("categoryIds[]", `${id}`));
  }

  const res = await fetchApi(`/api/v2/users/${id}`, "PATCH", formData);

  return res.json() as Promise<User>;
}

export async function deleteUser(id: number | string) {
  await fetchApi(`/api/v2/users/${id}`, "DELETE");
}

export async function deleteTag(id: number) {
  await fetchApi(`/api/v2/tags/${id}`, "DELETE");
}

export async function createTier(data: CreateTierData): Promise<TierDetails> {
  const res = await fetchApi("/api/v2/tiers", "POST", data);
  return res.json() as Promise<TierDetails>;
}

export async function updateTier(
  id: number,
  data: UpdateTierData,
): Promise<void> {
  await fetchApi(`/api/v2/tiers/${id}`, "PATCH", data);
}

export async function updateTierOrder(tierIds: number[]): Promise<void> {
  await fetchApi("/api/v2/tiers/order", "PATCH", { tierIds });
}

export async function updateGovernanceUnit(
  id: number,
  data: UpdateGovernanceUnitData,
): Promise<void> {
  await fetchApi(`/api/v2/governance-units/${id}`, "PATCH", data);
}

export async function createGovernanceUnit(
  data: CreateGovernanceUnitData,
): Promise<void> {
  await fetchApi("/api/v2/governance-units", "POST", data);
}

export async function deleteTier(id: number): Promise<void> {
  await fetchApi(`/api/v2/tiers/${id}`, "DELETE");
}

export async function getCurrentUser() {
  const res = await fetchApi(getCurrentUserEndpoint);
  return res.json() as Promise<CurrentUser>;
}

async function getRedirectUrl(path: string) {
  const res = await fetchApi(path);
  return res.json() as Promise<SsoDetails>;
}

export async function getRedirectUrlLogIn(
  email: string,
  redirectPath?: string,
) {
  const params = new URLSearchParams({ email });
  if (redirectPath) {
    params.append("redirectPath", redirectPath);
  }
  return getRedirectUrl(
    `/api/v2/sso/redirect-url-sign-in?${params.toString()}`,
  );
}

export async function getRedirectUrlActivate(token: string) {
  return getRedirectUrl(`/api/v2/sso/redirect-url-sign-up?token=${token}`);
}

export async function getRedirectUrlLinkIdentity() {
  return getRedirectUrl("/api/v2/sso/redirect-url-link-id");
}

export async function getRedirectUrlDefault(redirectPath?: string) {
  const params = new URLSearchParams();
  if (redirectPath) {
    params.append("redirectPath", redirectPath);
  }
  const queryString = params.toString();
  const res = await fetchApi(
    `/api/v2/sso/redirect-url-default${queryString ? `?${queryString}` : ""}`,
  );
  const data = (await res.json()) as { redirectUrl?: string };

  if (data.redirectUrl) {
    return { redirectUrl: data.redirectUrl };
  }

  throw new FetchError("NOT_FOUND");
}

export async function createDocument(data: FormData) {
  const res = await fetchApi("/api/v2/document-releases", "POST", data);
  return res.json() as Promise<{
    id: number;
    documentId: number;
    majorVersion: number;
    status: string;
    artifact: {
      id: number;
      title: string;
      status: string;
      fileType: string;
    };
  }>;
}

export async function uploadArtifactFile(artifactId: number, data: Blob) {
  await fetchApi(
    `/api/v2/document-artifacts/${artifactId}/upload`,
    "POST",
    data,
  );
}

export async function downloadArtifact(artifactId: number) {
  const response = await fetchApi(
    `/api/v2/document-artifacts/${artifactId}/download`,
  );
  return response.blob();
}

export interface UpdateDocumentData {
  ownerId?: number;
  publisherId?: number;
  categoryId?: number;
  tierId?: number | null;
  referenceNumber?: string | null;
  scheduleReviewPeriod?: string | null;
  tagIds?: number[];
  linkedDocumentIds?: number[];
}

// Fields editable one at a time through PATCH /documents/:id.
export type DocumentEditableField =
  | "categoryId"
  | "tierId"
  | "referenceNumber"
  | "scheduleReviewPeriod"
  | "ownerId"
  | "publisherId"
  | "tags"
  | "links";

export async function createDocumentShares(
  documentId: number,
  targetGovernanceUnitIds: number[],
): Promise<void> {
  await fetchApi(`/api/v2/documents/${documentId}/shares`, "POST", {
    targetGovernanceUnitIds,
  });
}

export async function inviteQuizTakers(quizId: number, userIds: number[]) {
  await fetchApi(`/api/v2/quizzes/${quizId}/invitations`, "POST", { userIds });
}

export async function generateQuiz(
  documentReleaseId: number,
  prompt?: string,
): Promise<{ id: number }> {
  const res = await fetchApi("/api/v2/quizzes/generate", "POST", {
    documentReleaseId,
    prompt,
  });
  return res.json() as Promise<{ id: number }>;
}

export async function updateQuiz(
  quizId: number,
  questions: {
    question: string;
    options: QuizAnswerOption[];
    reason: string | null;
  }[],
) {
  await fetchApi(`/api/v2/quizzes/${quizId}`, "PATCH", { questions });
}

export async function completeQuiz(quizId: number) {
  await fetchApi(`/api/v2/quizzes/${quizId}/completion`, "POST");
}

export async function updateDocument(
  documentId: number | string,
  data: UpdateDocumentData,
) {
  await fetchApi(`/api/v2/documents/${documentId}`, "PATCH", data);
}

interface PutStepUser {
  userId: number;
  permission: string;
}

export interface WorkflowStepData {
  id?: number;
  name: string;
  description: string | null;
  type: string;
  dueAt?: string | null;
  users: PutStepUser[];
}

export interface UpdateDocumentReleaseData {
  adoptedAt?: Date | null;
  reviewFrom?: Date | string | null;
  reviewTo?: Date | string | null;
  status?: "published";
  nextReviewFrom?: string | null;
  nextReviewTo?: string | null;
  description?: string | null;
  steps?: WorkflowStepData[];
}

// DocumentArtifactEntity fields, edited through PATCH /document-releases/:id.
export type ArtifactEditableField = "reviewFrom" | "reviewTo";

export async function updateDocumentRelease(
  releaseId: number,
  data: UpdateDocumentReleaseData,
) {
  await fetchApi(`/api/v2/document-releases/${releaseId}`, "PATCH", data);
}

export async function updateDocumentName(documentId: number, name: string) {
  await fetchApi(`/api/v2/documents/${documentId}/name`, "PATCH", { name });
}

export async function moveWorkflow(
  documentArtifactId: number,
  direction: "forward" | "backward" | "cancel",
) {
  await fetchApi(
    `/api/v2/workflows/${documentArtifactId}/transitions`,
    "POST",
    {
      direction,
    },
  );
}

export async function addReaders(releaseId: number, userIds: number[]) {
  await fetchApi(`/api/v2/document-releases/${releaseId}/readers`, "POST", {
    userIds,
  });
}

export async function deleteReader(releaseId: number, userId: number) {
  await fetchApi(
    `/api/v2/document-releases/${releaseId}/readers/${userId}`,
    "DELETE",
  );
}

export async function hasRead(releaseId: number, userId: number) {
  await fetchApi(
    `/api/v2/document-releases/${releaseId}/readers/${userId}`,
    "PATCH",
    { hasRead: true },
  );
}

export async function archiveDocument(documentId: number) {
  await fetchApi(`/api/v2/documents/${documentId}`, "DELETE");
}

export async function createWorkflowStepInstanceComment(
  stepId: number,
  type: WorkflowStepInstanceCommentType,
  text: string | null,
) {
  await fetchApi(`/api/v2/workflows/steps/${stepId}/comments`, "POST", {
    type,
    text,
  });
}

export async function createTags(tags: string[]): Promise<IdAndTitle[]> {
  const res = await fetchApi("/api/v2/tags/batch", "POST", { tags });
  return res.json() as Promise<IdAndTitle[]>;
}

export async function getOfficeURIScheme(artifactId: number) {
  const res = await fetchApi(
    `/api/v2/document-artifacts/${artifactId}/office-uri-scheme`,
  );
  return res.json() as Promise<{ uri: string | null }>;
}

export async function resolveDocumentIdFromOfficeUrl(officeUrl: string) {
  const params = new URLSearchParams({ url: officeUrl });
  const res = await fetchApi(
    `/api/v2/document-artifacts/resolve-document-id-from-office-url?${params.toString()}`,
  );
  return res.json() as Promise<{
    documentId: number | null;
    documentArtifactId: number | null;
  }>;
}

export async function createCategory(data: CategoryData) {
  await fetchApi("/api/v2/categories", "POST", data);
}

export async function updateCategory(id: number, data: CategoryData) {
  await fetchApi(`/api/v2/categories/${id}`, "PATCH", data);
}

export async function deleteCategory(id: number) {
  await fetchApi(`/api/v2/categories/${id}`, "DELETE");
}

export async function updateCategoryOrder(data: CategoryType[]) {
  await fetchApi("/api/v2/categories/order", "PATCH", data);
}

export async function updateDocumentOrder(
  data: { id: number; chartOrder: number }[],
) {
  await fetchApi("/api/v2/documents/order", "PATCH", data);
}

export async function savePinnedDocument(
  documentId: number,
  description: string | null,
) {
  await fetchApi(`/api/v2/documents/${documentId}/pin`, "PUT", {
    description: description,
  });
}

export async function unpinDocument(documentId: number) {
  await fetchApi(`/api/v2/documents/${documentId}/pin`, "DELETE");
}

export async function translateDocument(
  artifactId: number,
  targetLang: string,
  sourceLang?: string,
) {
  return fetchApi("/api/v2/translation/translate-document", "POST", {
    artifactId,
    targetLang,
    sourceLang,
  });
}

interface OAuthLoginResponse {
  redirectUrl: string;
  codeVerifier: string;
  state: string;
}

export async function getOAuthLogin(): Promise<OAuthLoginResponse> {
  const res = await fetchApi("/api/v2/sp/oauth/login");
  return res.json() as Promise<OAuthLoginResponse>;
}

interface UpdateOnboardingState {
  siteId?: string | null;
  driveId?: string | null;
  onboardingCompleted?: boolean;
}

export async function updateOnboardingState(data: UpdateOnboardingState) {
  await fetchApi("/api/v2/sp/onboarding-state", "PATCH", data);
}

export async function grantSpAccess() {
  const res = await fetchApi("/api/v2/sp/grant", "POST");
  return res.json() as Promise<{ permissionId: string }>;
}

export async function getSpSiteByUrl(siteUrl: string): Promise<SpSite> {
  const res = await fetchApi("/api/v2/sp/sites/validate", "POST", { siteUrl });
  return res.json() as Promise<SpSite>;
}

export async function generateDiffSummary(documentArtifactId: number) {
  await fetchApi(
    `/api/v2/document-artifacts/${documentArtifactId}/diff-summary/generate`,
    "PUT",
  );
}

export interface SearchHit {
  documentUrl: string;
  documentPath: string;
  documentTitle: string;
  documentSnippet: string;
  documentCaption?: string;
  documentCaptionHighlights?: string;
  h1: string | null;
  h2: string | null;
  h3: string | null;
  h4: string | null;
  h5: string | null;
  h6: string | null;
}

export async function searchDocuments(query: string): Promise<SearchHit[]> {
  const params = new URLSearchParams({ query });
  const res = await fetchApi(`/api/v2/search?${params.toString()}`);
  return res.json() as Promise<SearchHit[]>;
}

export async function getOnboardingOAuthLogin(setupToken: string): Promise<{
  redirectUrl: string;
}> {
  const res = await fetchApi(
    `/api/v2/onboarding/microsoft/oauth/login?setupToken=${encodeURIComponent(setupToken)}`,
  );
  return res.json() as Promise<{ redirectUrl: string }>;
}

export async function migrateAssignments(
  setupToken: string,
): Promise<{ migrated: number; failed: number }> {
  const res = await fetchApi(
    `/api/v2/onboarding/microsoft/${setupToken}/assignments/migrate`,
    "POST",
  );
  return res.json() as Promise<{ migrated: number; failed: number }>;
}

export async function startMigrationNow(setupToken: string): Promise<void> {
  await fetchApi(
    `/api/v2/onboarding/microsoft/${setupToken}/migration/start`,
    "POST",
  );
}

export async function createWorkflowTemplate(
  data: WorkflowTemplateData,
): Promise<WorkflowTemplate> {
  const res = await fetchApi("/api/v2/workflow-templates", "POST", data);
  return res.json() as Promise<WorkflowTemplate>;
}

export async function updateWorkflowTemplate(
  id: number,
  data: WorkflowTemplateData,
): Promise<WorkflowTemplate> {
  const res = await fetchApi(`/api/v2/workflow-templates/${id}`, "PUT", data);
  return res.json() as Promise<WorkflowTemplate>;
}

export async function duplicateWorkflowTemplate(
  id: number,
  name: string,
): Promise<WorkflowTemplate> {
  const res = await fetchApi(
    `/api/v2/workflow-templates/${id}/duplicate`,
    "POST",
    { name },
  );
  return res.json() as Promise<WorkflowTemplate>;
}

export async function deleteWorkflowTemplates(ids: number[]): Promise<void> {
  await fetchApi("/api/v2/workflow-templates", "DELETE", { ids });
}

export async function applyWorkflowTemplate(
  id: number,
  data: ApplyWorkflowTemplateData,
): Promise<void> {
  await fetchApi(`/api/v2/workflow-templates/${id}/apply`, "POST", data);
}
